// On-site training records (Session AFL): every training session ELE has delivered or scheduled,
// filterable by site, status, type and date, with the certificate reference each completed session
// carries. The site detail page links in here with ?siteId= (filtered) and ?siteId=&new=1 (the
// creation flow, pre-set to that site).
import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";
import { NewTrainingSessionModal } from "./NewTrainingSessionModal";
import {
  TRAINING_STATUS_LABELS,
  TRAINING_TYPES,
  TRAINING_TYPE_LABELS,
  trainingTypeLabel,
} from "../../lib/trainingTypes";
import type { TrainingSessionListItemDto, TrainingSessionStatus, TrainingType } from "../../lib/trainingTypes";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";

const inputCls =
  "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

const PAGE_SIZE = 20;

// The status pill takes the registry's `training` tone (scheduled blue, completed green, cancelled grey)
// with this page's own words (TRAINING_STATUS_LABELS).

export function TrainingPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const urlSiteId = params.get("siteId") ?? "";

  const [rows, setRows] = useState<TrainingSessionListItemDto[]>([]);
  // Card list below md, table otherwise -- decided once at mount (Session AHB).
  const [mobileCards] = useState(initialMobileLayout);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [siteId, setSiteId] = useState(urlSiteId);
  const [status, setStatus] = useState<TrainingSessionStatus | "">("");
  const [type, setType] = useState<TrainingType | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Session AFN: the "renewals due" pill + filter. renewalsDueCount is the tenant-wide backlog
  // (independent of the current filters); renewalDue toggles the list to just that set.
  const [renewalDue, setRenewalDue] = useState(false);
  const [renewalsDueCount, setRenewalsDueCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(params.get("new") === "1");


  // Follow the URL when the site page links in with a different ?siteId=.
  useEffect(() => {
    setSiteId(urlSiteId);
  }, [urlSiteId]);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    return api.training
      .list({
        page,
        pageSize: PAGE_SIZE,
        ...(siteId ? { siteId } : {}),
        ...(status ? { status } : {}),
        ...(type ? { type } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(renewalDue ? { renewalDue: true } : {}),
      })
      .then((r) => {
        setRows(r.items);
        setTotal(r.total);
        setRenewalsDueCount(r.renewalsDueCount);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [page, siteId, status, type, from, to, renewalDue]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [siteId, status, type, from, to, renewalDue]);

  function clearFilters() {
    setSiteId("");
    setStatus("");
    setType("");
    setFrom("");
    setTo("");
    setRenewalDue(false);
    // Drop the deep-link parameters too, so Clear filters really clears everything.
    setParams({}, { replace: true });
  }

  function closeModal() {
    setModalOpen(false);
    if (params.get("new")) {
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = Boolean(siteId || status || type || from || to || renewalDue);

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Training sessions"
        subtitle={
          <>
            On-site training delivered to a customer&apos;s staff.
            {/* Session AFN: the "renewals due" pill. Hidden when 0. Clicking it filters the list to the
                renewal-due set (and clicking again clears it).
                Session AJI correction, because this used to say the cron "emails" the same set: the
                cron now DRAFTS the reminder for the office to review and send. It still stamps
                renewalReminderSentAt, so a session leaves this count when a draft is RAISED, not when
                the customer is actually told. The pill answers "who still needs a reminder
                preparing". */}
            {renewalsDueCount > 0 && (
              <button
                type="button"
                aria-pressed={renewalDue}
                onClick={() => setRenewalDue((v) => !v)}
                className="mt-2 block max-sm:min-h-11"
              >
                <StatusPill
                  word={`${renewalsDueCount} renewal${renewalsDueCount === 1 ? "" : "s"} due within 60 days`}
                  tone="amber"
                  variant={renewalDue ? "solid" : "tint"}
                />
              </button>
            )}
          </>
        }
        actions={
          <Button size="sm" onClick={() => setModalOpen(true)} icon={<Plus aria-hidden size={14} />} className="max-sm:w-full">
            New training session
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-full min-w-0 sm:w-auto">
          <RecordPicker kind="site" id="training-filter-site" label="Site" value={siteId} placeholder="All sites (search to filter)" onChange={(id) => setSiteId(id)} />
        </div>
        <div className="w-full min-w-0 sm:w-auto">
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="training-filter-status">
            Status
          </label>
          <select
            id="training-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as TrainingSessionStatus | "")}
            className={`w-full sm:w-auto ${inputCls}`}
          >
            <option value="">All statuses</option>
            <option value="SCHEDULED">{TRAINING_STATUS_LABELS.SCHEDULED}</option>
            <option value="COMPLETED">{TRAINING_STATUS_LABELS.COMPLETED}</option>
            <option value="CANCELLED">{TRAINING_STATUS_LABELS.CANCELLED}</option>
          </select>
        </div>
        <div className="w-full min-w-0 sm:w-auto">
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="training-filter-type">
            Type
          </label>
          <select id="training-filter-type" value={type} onChange={(e) => setType(e.target.value as TrainingType | "")} className={`w-full sm:w-auto ${inputCls}`}>
            <option value="">All types</option>
            {TRAINING_TYPES.map((t) => (
              <option key={t} value={t}>
                {TRAINING_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full min-w-0 sm:w-auto">
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="training-filter-from">
            From
          </label>
          <input id="training-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`w-full sm:w-auto ${inputCls}`} />
        </div>
        <div className="w-full min-w-0 sm:w-auto">
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="training-filter-to">
            To
          </label>
          <input id="training-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`w-full sm:w-auto ${inputCls}`} />
        </div>
        {filtered && (
          <button type="button" onClick={clearFilters} className="font-menu text-xs tracking-wide text-ele-body max-sm:min-h-11">
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <Loader label="Loading training sessions..." className="py-6" />
      ) : error ? (
        <div role="alert" className="py-6 text-sm text-ele-strong">
          <p>Could not load the training records. Please try again in a moment.</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          role={filtered ? "status" : undefined}
          message={filtered ? "No training sessions match these filters." : "No training sessions recorded yet."}
        />
      ) : (
        <>
          {mobileCards ? (
            /* Session AHB, the worst of the ten: four columns shared 311px, leaving the DATE
               34px (one word) and the STATUS 34px for a pill that, sitting in an
               overflow-visible .cell-actions cell, painted over the type beside it rather
               than clipping. Site and type get their own lines here; the engineer and the
               certificate reference, both desktop-only columns, come back. */
            <MobileCardList label="Training sessions">
              {rows.map((row) => (
                <MobileListCard
                  key={row.id}
                  to={`/office/training/${encodeURIComponent(row.id)}`}
                  primary={row.siteName}
                  secondary={trainingTypeLabel(row.trainingType, row.customLabel)}
                  meta={
                    // "Not assigned" deliberately, never "Unassigned": since Session AGR an
                    // unassigned session reaches nobody's phone, so this reads as the gap it
                    // is. The wording matches the desktop cell exactly.
                    `${formatDate(row.sessionDate)} | ${row.assignedEngineerName ?? "Not assigned"}`
                  }
                  right={
                    <>
                      <StatusPill word={TRAINING_STATUS_LABELS[row.status]} tone={statusEntry("training", row.status).tone} />
                      {row.certificateRef && <span className="ele-ref text-xs">{row.certificateRef}</span>}
                    </>
                  }
                />
              ))}
            </MobileCardList>
          ) : (
          <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
            <table className="ele-list-table w-full text-left text-sm" aria-label="Training sessions">
              {/* Seven percentage columns summing to exactly 100: .ele-list-table is fixed-layout and
                  truncates rather than scrolling (the locked TABLE RULE), so the widths must balance.
                  Session AGM added "Assigned to" and re-balanced the other six to make room. It is
                  max-md:hidden because the mobile set stays Date / Site / Type / Status. */}
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "19%" }} />
                <col style={{ width: "21%" }} />
                <col className="max-md:hidden" style={{ width: "14%" }} />
                <col className="max-md:hidden" style={{ width: "8%" }} />
                <col style={{ width: "12%" }} />
                <col className="max-md:hidden" style={{ width: "14%" }} />
                <col className="w-8 md:hidden" />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Site</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="max-md:hidden px-3 py-2">Assigned to</th>
                  <th className="max-md:hidden px-3 py-2">Attendees</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="max-md:hidden px-3 py-2">Certificate</th>
                  <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                      navigate(`/office/training/${encodeURIComponent(row.id)}`);
                    }}
                  >
                    <td className="px-3 py-2 align-middle">{formatDate(row.sessionDate)}</td>
                    <td className="px-3 py-2 align-middle">{row.siteName}</td>
                    <td className="px-3 py-2 align-middle">{trainingTypeLabel(row.trainingType, row.customLabel)}</td>
                    <td className="max-md:hidden px-3 py-2 align-middle">
                      {row.assignedEngineerName ?? (
                        // Unassigned is not a gap to flag: the session is visible to EVERY engineer,
                        // so it says what it means rather than "Unassigned", which would read as
                        // something to go and fix.
                        //
                        // Session AGR: unassigned is no longer a deliberate "any engineer can pick
                        // it up" state, it is a gap -- the session reaches nobody until the office
                        // names someone. So this reads as the gap it now is, rather than AGN's
                        // "Any engineer", which would send the office away reassured.
                        <span className="text-ele-body" title="Not on any engineer's phone until it is assigned">
                          Not assigned
                        </span>
                      )}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-middle">{row.attendeeCount}</td>
                    <td className="cell-actions px-3 py-2 align-middle">
                      <StatusPill word={TRAINING_STATUS_LABELS[row.status]} tone={statusEntry("training", row.status).tone} />
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-middle">
                      {row.certificateRef ? <span className="ele-ref">{row.certificateRef}</span> : <span className="text-ele-body">-</span>}
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg
                        aria-hidden
                        viewBox="0 0 16 16"
                        className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green"
                      >
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-between text-sm text-ele-body">
              <span>
                {total} session{total === 1 ? "" : "s"}
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="font-menu text-xs tracking-wide disabled:opacity-40 max-sm:inline-flex max-sm:min-h-11 max-sm:items-center max-sm:px-4"
                >
                  Previous
                </button>
                <span>
                  Page {page} of {pages}
                </span>
                <button
                  type="button"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  className="font-menu text-xs tracking-wide disabled:opacity-40 max-sm:inline-flex max-sm:min-h-11 max-sm:items-center max-sm:px-4"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <NewTrainingSessionModal
        open={modalOpen}
        onClose={closeModal}
        onCreated={(id) => navigate(`/office/training/${encodeURIComponent(id)}`)}
        lockedSiteId={urlSiteId || null}
      />
    </div>
  );
}
